import { useEffect, useCallback, useState } from 'react';
import { gsap } from 'gsap'; // Ensure GSAP is installed

/**
 * Custom hook to manage a spotlight animation effect on a container.
 *
 * @param {React.RefObject<HTMLElement>} containerRef - Ref to the container element where the mouse moves.
 * @param {React.RefObject<HTMLElement>} spotlightRef - Ref to the spotlight element (the overlay div).
 * @param {boolean} hasHoveredProject - Boolean indicating if a project is currently hovered (for spotlight radius).
 * @returns {{x: number, y: number}} - Current coordinates of the spotlight.
 */
const useSpotlightEffect = (containerRef, spotlightRef, hasHoveredProject) => {
  const [spotlightCoordinates, setSpotlightCoordinates] = useState({ x: 0, y: 0 });

  // Initialize spotlight opacity to 0 on mount
  useEffect(() => {
    if (spotlightRef.current) {
      gsap.set(spotlightRef.current, { opacity: 0 });
    }
  }, [spotlightRef]);

  // Callback for mouse movement within the container
  const handleMouseMove = useCallback((e) => {
    if (!spotlightRef.current || !containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    // Only apply effect if container width is above a certain threshold (e.g., for desktop)
    // The 590px check was in your original code, so keeping it.
    if (rect.width < 590) {
      if (gsap.getProperty(spotlightRef.current, 'opacity') > 0) {
        gsap.to(spotlightRef.current, { opacity: 0, duration: 0.1 }); // Quickly fade out if resizing
      }
      return;
    }

    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const xPercent = (x / rect.width) * 100;
    const yPercent = (y / rect.height) * 100;

    const spotlightRadius = hasHoveredProject ? 30 : 15; // Radius changes based on hover state
    setSpotlightCoordinates({ x: x, y: y });

    gsap.to(spotlightRef.current, {
      clipPath: `circle(${spotlightRadius}px at ${xPercent}% ${yPercent}%)`,
      opacity: 1,
      duration: 0, // Instant update for position
      ease: "sine.out",
    });
  }, [containerRef, spotlightRef, hasHoveredProject]);

  // Callback for mouse leaving the container
  const handleMouseLeave = useCallback(() => {
    if (!spotlightRef.current) return;
    gsap.to(spotlightRef.current, {
      opacity: 0,
      duration: 0.3, // Smooth fade out
    });
  }, [spotlightRef]);

  // Attach/detach event listeners
  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('mousemove', handleMouseMove);
      container.addEventListener('mouseleave', handleMouseLeave);
    }

    return () => {
      if (container) {
        container.removeEventListener('mousemove', handleMouseMove);
        container.removeEventListener('mouseleave', handleMouseLeave);
      }
    };
  }, [containerRef, handleMouseMove, handleMouseLeave]);

  return spotlightCoordinates;
};

export default useSpotlightEffect;